import { useCurrentUser } from '@cma/app'
import { Button, Modal } from '@cma/common'
import { HelixIcon } from '@helix/helix-icon'
import {
  checkmark as CheckIcon,
  filter as FilterIcon
} from '@helix/helix-icon/outlined'
import { Select } from '@helix/select'
import { useState } from 'react'
import { ActionMeta, MultiValue, OptionProps } from 'react-select'
import { getLocationIcon } from './content/share_modal/utils'
import { usePlatformClient } from './usePlatformClient'

export interface Option {
  label: string
  value: {
    type: string
    id: string
  }
}

const defaultOption: Option = {
  label: 'All Locations',
  value: { type: 'all', id: '' }
}

interface FilterLocationsProps {
  onChange?: (selected: Option | null) => void
}

export function FilterLocations({ onChange }: FilterLocationsProps) {
  const { data: { currentUser } = {} } = useCurrentUser()
  const { data: { platformClient } = {} } = usePlatformClient({
    id: currentUser?.account?.id || ''
  })

  const [isOpen, setIsOpen] = useState(false)
  const [draftLocation, setDraftLocation] = useState<Option>(defaultOption)
  const [selectedLocation, setSelectedLocation] = useState<Option | null>(null)

  const options: Option[] = [
    defaultOption,
    ...(platformClient?.shareableTargets || [])
      // .filter((l) => l.entityType !== 'Account')
      .map((location) => {
        const prefix = `${location.entityType === 'brokerage' ? 'b' : 'o'}-`
        return {
          label: location.name || 'N/A',
          value: {
            type: location.entityType.toLowerCase(),
            id: `${prefix}${location.entityId}`
          }
        }
      })
  ]

  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setIsOpen(true)}>
        <div className="flex items-center gap-2">
          <span>{selectedLocation?.label || 'All Locations'}</span>
          <HelixIcon icon={FilterIcon} />
        </div>
        <span className="sr-only">Filter locations</span>
      </Button>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false)
          setDraftLocation(selectedLocation || defaultOption)
        }}>
        <Modal.Title weight={600}>Filter</Modal.Title>
        <div className="mt-6">
          <div className="mb-4 flex flex-col">
            <label htmlFor="location" className="helix-label">
              To
            </label>
            <Select
              isMulti
              aria-label="select location"
              placeholder="Add region, brokerage name, office, email, or agent name"
              getOptionValue={(option: Option) => option.value.id}
              getOptionLabel={(option: Option) => option.label}
              options={options}
              value={draftLocation}
              components={{
                Option: ({
                  data,
                  isSelected,
                  innerProps,
                  innerRef
                }: OptionProps<Option>) => (
                  <div
                    ref={innerRef}
                    {...innerProps}
                    className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-gray-100">
                    {getLocationIcon(data.value.type)}
                    <span className="helix-body--strong">{data.label}</span>
                    {isSelected && (
                      <HelixIcon
                        icon={CheckIcon}
                        className="helix-svg-fill--green-700 ml-auto"
                      />
                    )}
                  </div>
                )
              }}
              onChange={(selected: any, action: ActionMeta<Option>) => {
                const currentSelection = selected as MultiValue<Option>

                if (action.action === 'select-option') {
                  setDraftLocation(action.option as Option)
                  return
                }

                if (
                  action.action === 'remove-value' ||
                  action.action === 'pop-value' ||
                  action.action === 'clear' ||
                  currentSelection.length === 0
                ) {
                  setDraftLocation(currentSelection[0] || defaultOption)
                  return
                }

                if (currentSelection.length > 1) {
                  const last = currentSelection[currentSelection.length - 1]
                  setDraftLocation(last.value.id ? last : draftLocation)
                }
              }}
            />
          </div>
          <div className="-mx-6 flex items-center justify-end !border-t !border-solid !border-gray-100 px-5 pt-5">
            <Button
              onClick={() => {
                setIsOpen(false)
                setSelectedLocation(draftLocation)
                setDraftLocation(draftLocation)
                onChange?.({
                  ...draftLocation,
                  value: {
                    ...draftLocation.value,
                    id: draftLocation.value.id.replace(/^(b|o|a)-/, '')
                  }
                })
              }}>
              Apply
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}
